// Tela de gestão de roles do Hub (PO1-2044 + CRUD).
// Lista AbpRoles com count de usuários, permite criar / editar dados básicos /
// editar permissões / excluir (papéis estáticos não podem ser excluídos).

import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import ReactTable from "react-table";

import { listRoles, deleteRole } from "services/rolesApi";
import { useToast } from "hooks/toast";
import EditRolePermissionsModal from "./components/EditRolePermissionsModal";
import EditRoleModal from "./components/EditRoleModal";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPermissionsRole, setEditingPermissionsRole] = useState(null);
  const [editingRole, setEditingRole] = useState(undefined); // undefined=closed, null=create, obj=edit
  const { addToast } = useToast();

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listRoles();
      setRoles(data.items || []);
    } catch (err) {
      console.error("[Roles] loadRoles:", err);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: "Erro ao carregar papéis",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleEditPermissions = useCallback((row) => {
    setEditingPermissionsRole(row.original);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingRole(row.original);
  }, []);

  const handleNew = useCallback(() => {
    setEditingRole(null);
  }, []);

  const handleClosePermissions = useCallback(() => {
    setEditingPermissionsRole(null);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingRole(undefined);
  }, []);

  const handleSaved = useCallback(() => {
    loadRoles();
  }, [loadRoles]);

  const handleDelete = useCallback(
    async (row) => {
      const { Id, Name, DisplayName, IsStatic, userCount } = row.original;
      if (IsStatic) {
        addToast({
          place: "tr",
          type: "warning",
          title: "Não permitido",
          description: "Papéis estáticos não podem ser excluídos.",
          icon: "tim-icons icon-alert-circle-exc",
        });
        return;
      }
      const label = DisplayName || Name;
      const warning =
        userCount > 0
          ? `Tem certeza? "${label}" tem ${userCount} usuário(s) vinculado(s). O vínculo persiste no banco mas o papel some das listagens.`
          : `Excluir o papel "${label}"?`;
      // eslint-disable-next-line no-alert
      if (!window.confirm(warning)) return;
      try {
        await deleteRole(Id);
        addToast({
          place: "tr",
          type: "success",
          title: "Excluído",
          description: `"${label}" removido.`,
          icon: "tim-icons icon-check-2",
        });
        loadRoles();
      } catch (err) {
        console.error("[Roles] delete:", err);
        const message =
          (err.response && err.response.data && err.response.data.error) ||
          "Erro ao excluir papel";
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: message,
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
    },
    [addToast, loadRoles]
  );

  const columns = [
    {
      Header: "Nome",
      accessor: "Name",
      headerClassName: "text-center",
    },
    {
      Header: "Nome de exibição",
      accessor: "DisplayName",
      headerClassName: "text-center",
    },
    {
      Header: "Usuários",
      accessor: "userCount",
      headerClassName: "text-center",
      Cell: (row) => <div className="text-center">{row.original.userCount}</div>,
      maxWidth: 110,
    },
    {
      Header: "Estático",
      accessor: "IsStatic",
      headerClassName: "text-center",
      Cell: (row) => (
        <div className="text-center">{row.original.IsStatic ? "Sim" : "Não"}</div>
      ),
      maxWidth: 110,
    },
    {
      Header: "Ações",
      accessor: "actions",
      headerClassName: "text-center",
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Button
            className="btn-icon btn-round btn-info"
            size="sm"
            onClick={() => handleEdit(row)}
            title="Editar dados do papel"
          >
            <i className="tim-icons icon-settings" />
          </Button>
          <Button
            className="btn-icon btn-round btn-success"
            size="sm"
            onClick={() => handleEditPermissions(row)}
            title="Editar permissões do Hub"
          >
            <i className="tim-icons icon-key-25" />
          </Button>
          <Button
            className="btn-icon btn-round btn-danger"
            size="sm"
            onClick={() => handleDelete(row)}
            disabled={row.original.IsStatic}
            title={
              row.original.IsStatic
                ? "Papel estático não pode ser excluído"
                : "Excluir papel"
            }
          >
            <i className="tim-icons icon-trash-simple" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="content">
      <Row>
        <Col className="ml-auto mr-auto" md="10">
          <h2 className="text-center">Papéis e Permissões do Hub</h2>
        </Col>
        <Col md="2" className="text-right">
          <Button color="success" onClick={handleNew}>
            <i className="tim-icons icon-simple-add" /> Novo
          </Button>
        </Col>
      </Row>
      <Row>
        <Card>
          <CardBody className="text-center">
            <ReactTable
              data={roles}
              columns={columns}
              defaultPageSize={10}
              loading={loading}
              showPaginationBottom
              previousText="Anterior"
              nextText="Próximo"
              loadingText="Carregando..."
              noDataText="Nenhum papel cadastrado"
              pageText="Página"
              ofText="de"
              rowsText="linhas"
              rowsSelectorText="linhas por página"
              className="-striped -highlight"
              resizable={true}
            />
          </CardBody>
        </Card>
      </Row>

      <EditRolePermissionsModal
        isOpen={!!editingPermissionsRole}
        role={editingPermissionsRole}
        onClose={handleClosePermissions}
        onSaved={handleSaved}
      />

      <EditRoleModal
        isOpen={editingRole !== undefined}
        role={editingRole}
        onClose={handleCloseEdit}
        onSaved={handleSaved}
      />
    </div>
  );
}
